/*
 * カート同意チェック（利用規約／プライバシーポリシー）共通ロジック
 * 対象: snippets/cart.liquid（カートページ）/ snippets/popup-cart.liquid（ミニカート）/
 *       snippets/product-direct-checkout-modal.liquid（直接チェックアウトモーダル）
 * 各画面の同意チェックボックス UI は snippets/cart-consent-checkbox.liquid を共有で使う。
 *
 * 同意日時の正（一次情報源）は n8n（eca-cart-affiliation webhook）のサーバー受信時刻であり、
 * cart attribute の consent_accepted_at はクライアント時計に基づく参考値にすぎない
 * （端末時計のズレ・改ざんの可能性があるため、監査上の正としては扱わない）。
 *
 * consent_version はテーマ設定 settings.layout_policy_reconsent_version の値をそのまま使う。
 * プライバシーポリシー再同意モーダル（DEV-3 snippets/popup-policy-reconsent.liquid）と
 * 同じ版番号を共有し、同一の改定に対する同意記録として扱う。
 */
(function() {
  'use strict';

  var TIMEOUT_MS = 10000;
  var CART_UPDATE_URL = '/cart/update.js';

  /* ---- スコープ解決 ---- */
  /* クリック／送信された要素から見て、同意チェックボックス・エラー表示・送信ボタンをまとめて
     探すための基準要素（form か dialog。なければ document 全体）を返す */
  function findScope(el) {
    if (!el || typeof el.closest !== 'function') return document;
    return el.closest('form, dialog') || document;
  }

  function getRoot(scope) {
    return scope.querySelector('[data-cart-consent-root]');
  }

  function getCheckbox(scope) {
    return scope.querySelector('[data-cart-consent-checkbox]');
  }

  /* 同意チェックボックスが描画されているか（= 同意取得が必須な画面か） */
  function isRequired(scope) {
    return !!getCheckbox(scope);
  }

  function isAccepted(scope) {
    var checkbox = getCheckbox(scope);
    return !!(checkbox && checkbox.checked);
  }

  /* テーマ設定 layout_policy_reconsent_version 由来の版番号（未設定時は空文字） */
  function getVersion(scope) {
    var root = getRoot(scope);
    if (!root) return '';
    var version = root.getAttribute('data-consent-version');
    return version ? version.trim() : '';
  }

  function showError(scope) {
    var errorEl = scope.querySelector('[data-cart-consent-error]');
    var checkbox = getCheckbox(scope);
    if (errorEl) errorEl.classList.add('is-visible');
    if (checkbox) {
      checkbox.setAttribute('aria-invalid', 'true');
      checkbox.focus();
    }
    var root = getRoot(scope);
    if (root && typeof root.scrollIntoView === 'function') {
      root.scrollIntoView({ block: 'center' });
    }
  }

  function clearError(scope) {
    var errorEl = scope.querySelector('[data-cart-consent-error]');
    var updateErrorEl = scope.querySelector('[data-cart-consent-update-error]');
    var checkbox = getCheckbox(scope);
    if (errorEl) errorEl.classList.remove('is-visible');
    if (updateErrorEl) updateErrorEl.classList.remove('is-visible');
    if (checkbox) checkbox.removeAttribute('aria-invalid');
  }

  /* ミニカートの /cart/update.js 通信エラー・検証失敗専用の表示。
     同意状態そのものは正しい（同意済みだから記録を試みた）ため、showError と異なり
     チェックボックスへ aria-invalid は付けない・フォーカスも移さない（誤って
     「同意していない」という趣旨に読めるのを避ける） */
  function showUpdateError(scope) {
    var updateErrorEl = scope.querySelector('[data-cart-consent-update-error]');
    if (updateErrorEl) updateErrorEl.classList.add('is-visible');
    var root = getRoot(scope);
    if (root && typeof root.scrollIntoView === 'function') {
      root.scrollIntoView({ block: 'center' });
    }
  }

  /* 送信ボタンの aria-disabled を同意状態に追随させる（視覚的なフィードバック用。
     実際のブロックは click/submit のガードが担うため、ここでは表示上の状態同期のみ） */
  function updateSubmitAria(scope) {
    var submitEl = scope.querySelector('[data-cart-consent-submit]');
    if (!submitEl) return;
    if (isRequired(scope) && !isAccepted(scope)) {
      submitEl.setAttribute('aria-disabled', 'true');
    } else {
      submitEl.removeAttribute('aria-disabled');
    }
  }

  /*
   * cart attribute へ書き込む値を組み立てる。
   * - チェックボックス非描画（同意取得不要な画面）→ 空文字で明示的に既存値を削除する
   *   （Shopify は cart attribute に空文字を渡すとそのキーごと削除する仕様のため）
   * - 同意済み かつ version 設定済み → 記録する
   * - version 未設定（テーマ設定未入力）→ null を返し、呼び出し側は何も記録しない
   *   （版が空の「同意」を記録すると、あとから何の改定に同意したか判別できなくなるため）
   * - 上記以外（未同意のまま呼ばれた場合。通常は呼び出し前段のガードで止まるため到達しない
   *   想定だが、念のため何も記録しない）
   */
  function buildAttributes(scope) {
    if (!isRequired(scope)) {
      return { consent_version: '', consent_accepted_at: '' };
    }
    if (!isAccepted(scope)) {
      return null;
    }
    var version = getVersion(scope);
    if (!version) {
      return null;
    }
    var acceptedAt;
    try {
      acceptedAt = new Date().toISOString();
    } catch (e) {
      acceptedAt = String(Date.now());
    }
    return { consent_version: version, consent_accepted_at: acceptedAt };
  }

  /* cart attribute を記録したケースのみ、反映結果（/cart/update.js のレスポンス）を検証する */
  function verify(cartJson, scope) {
    var shouldHaveRecorded = isRequired(scope) && isAccepted(scope) && !!getVersion(scope);
    if (!shouldHaveRecorded) return true;
    var version = getVersion(scope);
    return !!(cartJson && cartJson.attributes && cartJson.attributes.consent_version === version);
  }

  /* eca-cart-affiliation webhook へ送る consent_version（同意済みでなければ null） */
  function consentVersionForWebhook(scope) {
    if (isRequired(scope) && isAccepted(scope)) {
      var version = getVersion(scope);
      if (version) return version;
    }
    return null;
  }

  /* ミニカートの多重送信ガード（連打対策）。/cart/update.js の応答待ち中に
     同じ送信が重複して走ると cart attribute の書き込みが競合しうるため、
     モジュールスコープの boolean で処理中フラグを持つ */
  var minicartSubmitInProgress = false;

  /* ---- ミニカート専用: /cart/update.js 経由で同意情報を記録してからチェックアウトへ ---- */
  function handleMinicartSubmit(scope, form) {
    if (minicartSubmitInProgress) return;
    minicartSubmitInProgress = true;

    var submitEl = scope.querySelector('[data-cart-consent-submit]');
    if (submitEl) submitEl.setAttribute('aria-busy', 'true');

    var attributes = buildAttributes(scope);

    /* version 未設定で記録しないケースは、AJAXを介さず通常のフォーム送信を続行する。
       form.submit() は同期的にページ遷移（/cart への POST）を開始するため、
       以降クリックを受け付ける余地がなくなる。フラグ・aria-busy のリセットは不要 */
    if (attributes === null) {
      form.submit();
      return;
    }

    var controller = new AbortController();
    var timeoutId = setTimeout(function() { controller.abort(); }, TIMEOUT_MS);

    fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      signal: controller.signal
    })
      .then(function(res) {
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function(json) {
        if (!verify(json, scope)) throw new Error('consent verify failed');
        /* 成功時はそのまま /checkout へ離脱するため、フラグ・aria-busy は解除しない。
           このページはこの後すぐ破棄されるため、解除しなくても後続クリックには影響しない
           （逆に解除すると、遷移完了までの一瞬に連打が通ってしまう） */
        window.location.href = '/checkout';
      })
      .catch(function() {
        clearTimeout(timeoutId);
        minicartSubmitInProgress = false;
        if (submitEl) submitEl.removeAttribute('aria-busy');
        /* 通信エラー・検証失敗時は「通信エラー」専用の表示を使う。同意状態は正しいため
           showError（同意エラー）を流用すると「同意していない」という誤ったメッセージになる */
        showUpdateError(scope);
      });
  }

  /* ---- 初期化 ---- */

  /* クリック時ガード: 未同意のままチェックアウト系ボタンが押された場合はブロックする。
     capture:true で document に登録することで、cart-affiliation-script.liquid 等の
     ボタン自身に登録された click ハンドラより先に評価される */
  document.addEventListener('click', function(e) {
    var submitEl = e.target.closest && e.target.closest('[data-cart-consent-submit]');
    if (!submitEl) return;
    var scope = findScope(submitEl);
    if (isRequired(scope) && !isAccepted(scope)) {
      e.preventDefault();
      e.stopPropagation();
      showError(scope);
      return;
    }
    clearError(scope);
  }, true);

  /* 送信時ガード: click ガードをすり抜けるフォーム送信（Enterキー等）に対する多重防御と、
     ミニカートの同意情報記録（AJAX）を担う */
  document.addEventListener('submit', function(e) {
    var form = e.target;
    if (!form || typeof form.querySelector !== 'function') return;

    /*
     * form 内に複数の submit ボタンがある場合（例: snippets/cart.liquid の数量「更新」ボタンと
     * 「チェックアウト」ボタン）、e.submitter が data-cart-consent-submit を持たないボタン
     * （更新ボタン等）でも form.querySelector('[data-cart-consent-submit]') に無条件で
     * フォールバックすると、同意対象外の送信までブロックしてしまう。
     * 実際に押された／暗黙送信の既定ボタンである submitter を特定し、それが
     * data-cart-consent-submit にマッチする場合のみガード対象とする。
     */
    var submitter = e.submitter || form.querySelector('input[type="submit"], button[type="submit"], button:not([type])');
    if (!submitter || !(submitter.matches && submitter.matches('[data-cart-consent-submit]'))) return;
    var submitEl = submitter;

    var scope = findScope(submitEl);
    if (isRequired(scope) && !isAccepted(scope)) {
      e.preventDefault();
      e.stopPropagation();
      showError(scope);
      return;
    }
    clearError(scope);

    if (submitEl.getAttribute('data-cart-consent-record') === 'minicart') {
      e.preventDefault();
      handleMinicartSubmit(scope, form);
    }
  }, true);

  /* チェックボックス変更時: エラー表示解除 + 送信ボタンの aria-disabled 追随 */
  document.addEventListener('change', function(e) {
    var checkbox = e.target.closest && e.target.closest('[data-cart-consent-checkbox]');
    if (!checkbox) return;
    var scope = findScope(checkbox);
    clearError(scope);
    updateSubmitAria(scope);
  });

  /* 初期表示時点の aria-disabled 状態を同期しておく */
  var initialCheckboxes = document.querySelectorAll('[data-cart-consent-checkbox]');
  for (var i = 0; i < initialCheckboxes.length; i++) {
    updateSubmitAria(findScope(initialCheckboxes[i]));
  }

  /* 初期化完了フラグ。assets/cart-consent.css の
     html:not(.cart-consent-ready) form:has(...) によるフェイルクローズ（未チェック時の
     ボタン無効化）を解除する */
  document.documentElement.classList.add('cart-consent-ready');

  window.ECAConsent = {
    findScope: findScope,
    isRequired: isRequired,
    isAccepted: isAccepted,
    getVersion: getVersion,
    showError: showError,
    clearError: clearError,
    buildAttributes: buildAttributes,
    verify: verify,
    consentVersionForWebhook: consentVersionForWebhook
  };
})();
